import { PlusIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * One labelled row of controls, with an optional `+` at the end that adds
 * another one.
 *
 * The three rows — Demand, Filter, View — answer three different questions
 * (which data, which rows, how shown), and giving each its own line with its own
 * label is what stops them reading as one undifferentiated bar of chips. Every
 * control inside is the same height so the rows stay compact and aligned.
 */
export function ControlRow({
  label,
  add,
  children,
}: React.PropsWithChildren<{
  label: string;
  /** The `+` menu trigger. Omitted where there is nothing to add. */
  add?: React.ReactNode;
}>) {
  return (
    <div className="grid grid-cols-1 items-start gap-1 sm:grid-cols-[3.5rem_minmax(0,1fr)] sm:gap-2">
      <span className="flex h-7 items-center text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {children}
        {add}
      </div>
    </div>
  );
}

/**
 * The `+` trigger itself, so all three rows get an identical affordance.
 *
 * Every prop is forwarded, `ref` included. This sits inside a
 * `<PopoverTrigger asChild>`, which works by cloning its child with the props
 * that make it a trigger — an onClick, a ref, the aria/data state attributes. A
 * component that accepts only its own props swallows all of that and renders a
 * button that looks right and does nothing.
 */
export function AddButton({
  label,
  className,
  ...props
}: React.ComponentProps<typeof Button> & { label: string }) {
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      aria-label={label}
      className={cn('size-7 shrink-0 p-0 shadow-none', className)}
      {...props}
    >
      <PlusIcon className="h-3 w-3" />
    </Button>
  );
}

/**
 * Strips the frame off a control that sits inside a labelled chip.
 *
 * The chip already draws a border, and shadcn's inputs, selects and popover
 * triggers each draw their own — so every filter was rendering a box inside a
 * box. The chip owns the frame; the control inside it is just the value.
 */
export const CHIP_CONTROL =
  '[&_button]:h-6 [&_button]:border-0 [&_button]:px-1 [&_button]:text-xs [&_button]:shadow-none ' +
  '[&_input]:h-6 [&_input]:border-0 [&_input]:px-1 [&_input]:text-xs [&_input]:shadow-none ' +
  '[&_[data-slot=select-trigger]]:h-6 [&_[data-slot=select-trigger]]:border-0 ' +
  '[&_[data-slot=select-trigger]]:px-1 [&_[data-slot=select-trigger]]:shadow-none';

/** A control chip, matching the height and border of everything else in a row. */
export function ControlChip({
  children,
  onRemove,
  removeLabel,
}: React.PropsWithChildren<{
  onRemove?: () => void;
  removeLabel?: string;
}>) {
  return (
    <div className="flex h-7 items-center gap-1 rounded-md border bg-background px-2 text-xs">
      {children}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel}
          className="text-muted-foreground hover:text-foreground"
        >
          ×
        </button>
      ) : null}
    </div>
  );
}
